import { getAuthentication } from "../../lib/auth";
import { BETTING_WEBSITE_URL } from "../../lib/env";
import { escapeGqlString, gqlRequest } from "../../lib/functions";
import { BETTINGWEBSITEGQLSCHEMA } from "../../lib/gql/BettingWebsite";
import {
  IBettingWebsite,
  IBettingWebsiteRatingSubRates,
} from "../../types/BettingWebsite";
import { IMultiLanguageInputArgs } from "../../types/MultiLanguageInput";

export async function getBettingWebsites(
  limit: number,
  offset: number,
  query: string
): Promise<IBettingWebsite[]> {
  let filters = "";
  if (query && query.trim() !== "") {
    filters = `,query:"${query}"`;
  }
  const gqlString = `query{
    GetBettingWebsites(limit:${limit},offset:${offset}${filters}){
        ${BETTINGWEBSITEGQLSCHEMA}
    }
  }`;
  try {
    const response = await gqlRequest<{
      GetBettingWebsites: IBettingWebsite[];
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.GetBettingWebsites || [];
  } catch (err) {
    throw err;
  }
}

export async function getBettingWebsiteByID(
  id: string
): Promise<IBettingWebsite | null> {
  const gqlString = `query{
        GetBettingWebsiteByID(id:"${id}"){
            ${BETTINGWEBSITEGQLSCHEMA}
        }
    }`;

  try {
    const response = await gqlRequest<{
      GetBettingWebsiteByID: IBettingWebsite;
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.GetBettingWebsiteByID || null;
  } catch (err) {
    throw err;
  }
}

export async function addNewBettingWebsite(
  title: IMultiLanguageInputArgs
): Promise<IBettingWebsite | null> {
  const gqlString = `mutation{
        AddNewBettingWebsite(title:{
            en:"${escapeGqlString(title.en)}",
            ar:"${escapeGqlString(title.ar)}",
            fr:"${escapeGqlString(title.fr)}"
        }){
            ${BETTINGWEBSITEGQLSCHEMA}    
        }
    }`;

  try {
    const response = await gqlRequest<{
      AddNewBettingWebsite: IBettingWebsite;
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.AddNewBettingWebsite || null;
  } catch (err) {
    throw err;
  }
}

export async function editBettingWebsiteTitle(
  id: string,
  title: IMultiLanguageInputArgs
): Promise<IBettingWebsite | null> {
  const gqlString = `mutation{
    EditBettingWebsiteTitle(id:"${id}"title:{
        en:"${escapeGqlString(title.en)}",
        ar:"${escapeGqlString(title.ar)}",
        fr:"${escapeGqlString(title.fr)}"
    }){
        ${BETTINGWEBSITEGQLSCHEMA}    
    }
}`;

  try {
    const response = await gqlRequest<{
      EditBettingWebsiteTitle: IBettingWebsite;
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.EditBettingWebsiteTitle || null;
  } catch (err) {
    throw err;
  }
}

export async function editShortDescriptionBettingWebsite(
  id: string,
  description: IMultiLanguageInputArgs
): Promise<IBettingWebsite | null> {
  const gqlString = `mutation{
        EditShortDescriptionBettingWebsite(id:"${id}",description:{
            en:"${escapeGqlString(description.en)}",
            ar:"${escapeGqlString(description.ar)}",
            fr:"${escapeGqlString(description.fr)}"
        }){
            ${BETTINGWEBSITEGQLSCHEMA}    
        }
    }`;

  try {
    const response = await gqlRequest<{
      EditShortDescriptionBettingWebsite: IBettingWebsite;
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.EditShortDescriptionBettingWebsite || null;
  } catch (err) {
    throw err;
  }
}

export async function editLongDescriptionBettingWebsite(
  id: string,
  description: IMultiLanguageInputArgs
): Promise<IBettingWebsite | null> {
  const gqlString = `mutation{
          EditLongDescriptionBettingWebsite(id:"${id}",description:{
              en:"${escapeGqlString(description.en)}",
              ar:"${escapeGqlString(description.ar)}",
              fr:"${escapeGqlString(description.fr)}"
          }){
              ${BETTINGWEBSITEGQLSCHEMA}    
          }
      }`;

  try {
    const response = await gqlRequest<{
      EditLongDescriptionBettingWebsite: IBettingWebsite;
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.EditLongDescriptionBettingWebsite || null;
  } catch (err) {
    throw err;
  }
}

export async function updateBettingWebsiteImage(
  id: string,
  image: string
): Promise<IBettingWebsite | null> {
  const gqlString = `mutation{
    UpdateBettingWebsiteImage(id:"${id}",image:"${image}"){
        ${BETTINGWEBSITEGQLSCHEMA}
    }
  }`;

  try {
    const response = await gqlRequest<{
      UpdateBettingWebsiteImage: IBettingWebsite;
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.UpdateBettingWebsiteImage || null;
  } catch (err) {
    throw err;
  }
}

export async function editBettingWebsiteContacts(
  id: string,
  contacts: {
    Email: string;
    Web: string;
    Facebook: string;
    Telegram: string;
    Instagram: string;
    X: string;
  }
): Promise<IBettingWebsite | null> {
  const gqlString = `mutation{
        EditBettingWebsiteContacts(id:"${id}",
            email:"${contacts.Email}",
            web:"${contacts.Web}",
            facebook:"${contacts.Facebook}",
            telegram:"${contacts.Telegram}",
            instagram:"${contacts.Instagram}",
            x:"${contacts.X}"
        ){
            ${BETTINGWEBSITEGQLSCHEMA}    
        }
    }`;
  try {
    const response = await gqlRequest<{
      EditBettingWebsiteContacts: IBettingWebsite;
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.EditBettingWebsiteContacts || null;
  } catch (err) {
    throw err;
  }
}

export async function pushIntoWebsiteBettingFeatures(
  id: string,
  feature: string
): Promise<IBettingWebsite | null> {
  const gqlString = `mutation{
        PushIntoWebsiteBettingFeatures(id:"${id}",feature:"${feature}"){
            ${BETTINGWEBSITEGQLSCHEMA}
        }
    }`;
  try {
    const response = await gqlRequest<{
      PushIntoWebsiteBettingFeatures: IBettingWebsite;
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.PushIntoWebsiteBettingFeatures || null;
  } catch (err) {
    throw err;
  }
}

export async function pullFromWebsiteBettingFeatures(
  id: string,
  feature: string
): Promise<IBettingWebsite | null> {
  const gqlString = `mutation{
          PullFromWebsiteBettingFeatures(id:"${id}",feature:"${feature}"){
              ${BETTINGWEBSITEGQLSCHEMA}
          }
      }`;
  try {
    const response = await gqlRequest<{
      PullFromWebsiteBettingFeatures: IBettingWebsite;
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.PullFromWebsiteBettingFeatures || null;
  } catch (err) {
    throw err;
  }
}

export async function pushIntoBettingWebsiteLanguages(
  id: string,
  language: string
): Promise<IBettingWebsite | null> {
  const gqlString = `mutation{
          PushIntoBettingWebsiteLanguages(id:"${id}",language:"${language}"){
              ${BETTINGWEBSITEGQLSCHEMA}
          }
      }`;
  try {
    const response = await gqlRequest<{
      PushIntoBettingWebsiteLanguages: IBettingWebsite;
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.PushIntoBettingWebsiteLanguages || null;
  } catch (err) {
    throw err;
  }
}

export async function pullFromBettingWebsiteLanguages(
  id: string,
  language: string
): Promise<IBettingWebsite | null> {
  const gqlString = `mutation{
            PullFromBettingWebsiteLanguages(id:"${id}",language:"${language}"){
                ${BETTINGWEBSITEGQLSCHEMA}
            }
        }`;
  try {
    const response = await gqlRequest<{
      PullFromBettingWebsiteLanguages: IBettingWebsite;
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.PullFromBettingWebsiteLanguages || null;
  } catch (err) {
    throw err;
  }
}

export async function pushIntoBettingWebsitePaymentGateways(
  id: string,
  payment_gateway: string
): Promise<IBettingWebsite | null> {
  const gqlString = `mutation{
            PushIntoBettingWebsitePaymentGateways(id:"${id}",payment_gateway:"${payment_gateway}"){
                ${BETTINGWEBSITEGQLSCHEMA}
            }
        }`;
  try {
    const response = await gqlRequest<{
      PushIntoBettingWebsitePaymentGateways: IBettingWebsite;
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.PushIntoBettingWebsitePaymentGateways || null;
  } catch (err) {
    throw err;
  }
}

export async function pullFromBettingWebsitePaymentGateways(
  id: string,
  payment_gateway: string
): Promise<IBettingWebsite | null> {
  const gqlString = `mutation{
              PullFromBettingWebsitePaymentGateways(id:"${id}",payment_gateway:"${payment_gateway}"){
                  ${BETTINGWEBSITEGQLSCHEMA}
              }
          }`;
  try {
    const response = await gqlRequest<{
      PullFromBettingWebsitePaymentGateways: IBettingWebsite;
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.PullFromBettingWebsitePaymentGateways || null;
  } catch (err) {
    throw err;
  }
}

export async function editBettingWebsiteRating(
  id: string,
  rates: IBettingWebsiteRatingSubRates
): Promise<IBettingWebsite | null> {
  const gqlString = `mutation{
        EditBettingWebsiteRating(
            id:"${id}",
            secuirity:${rates.Secuirity},
            reputation:${rates.Reputation},
            transparency:${rates.Transparency},
            popularity:${rates.Popularity},
            user_experience:${rates.UserExperience},
            user_interface:${rates.UserInterface},
            bonus_amount:${rates.BonusAmount},
            variety_of_options:${rates.VarietyOfOptions},
            market_diversity:${rates.MarketDiversity},
            transaction_speed:${rates.TransactionSpeed},
            betting_odds:${rates.BettingOdds},
            support_quality:${rates.SupportQuality},
            bonus_type:${rates.BonusType},
            variety_of_transaction_methods:${rates.VarietyOfTransactionMethods}
        ){
            ${BETTINGWEBSITEGQLSCHEMA}    
        }
    }`;

  try {
    const response = await gqlRequest<{
      EditBettingWebsiteRating: IBettingWebsite;
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.EditBettingWebsiteRating || null;
  } catch (err) {
    throw err;
  }
}

export async function pushIntoBettingWebsiteQANDA(
  id: string,
  question: string,
  answer: string
): Promise<IBettingWebsite | null> {
  const gqlString = `mutation{
        PushIntoBettingWebsiteQANDA(id:"${id}",question:"${question}",answer:"${answer}"){
            ${BETTINGWEBSITEGQLSCHEMA}
        }
    }`;
  try {
    const response = await gqlRequest<{
      PushIntoBettingWebsiteQANDA: IBettingWebsite;
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.PushIntoBettingWebsiteQANDA || null;
  } catch (err) {
    throw err;
  }
}

export async function pullFromBettingWebsiteQANDA(
  id: string,
  question: string,
  answer: string
): Promise<IBettingWebsite | null> {
  const gqlString = `mutation{
          PullFromBettingWebsiteQANDA(id:"${id}",question:"${question}",answer:"${answer}"){
              ${BETTINGWEBSITEGQLSCHEMA}
          }
      }`;
  try {
    const response = await gqlRequest<{
      PullFromBettingWebsiteQANDA: IBettingWebsite;
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.PullFromBettingWebsiteQANDA || null;
  } catch (err) {
    throw err;
  }
}

export async function deleteWebsiteByID(
  id: string
): Promise<boolean | undefined> {
  const gqlString = `mutation{
        DeleteBettingWebsiteByID(id:"${id}")
    }`;
  try {
    const response = await gqlRequest<{
      DeleteBettingWebsiteByID: boolean;
    }>({
      query: gqlString,
      endpoint: BETTING_WEBSITE_URL,
      headers: {
        authorization: `Bearer ${getAuthentication()}`,
      },
    });
    if (response.errors && response.errors.length > 0) {
      throw new Error(response.errors[0].message);
    }
    return response?.data?.DeleteBettingWebsiteByID;
  } catch (err) {
    throw err;
  }
}
